import { zodResolver } from "@hookform/resolvers/zod";
import { Close } from "@mui/icons-material";
import { Button } from "@mui/material";
import { pick } from "lodash";
import { useSnackbar } from "notistack";
import { ReactElement, useEffect, useState } from "react";
import { useForm, useFieldArray, FormProvider, Control } from "react-hook-form";

import {
  TargetingForm,
  SavedSearchSchema,
  TargetingFormSchema,
} from "@stw/yup-validators";

import {
  UpsertAudienceSearchUserFilterMutation,
  UpsertAudienceSearchUserFilterMutationVariables,
  UpdateAudienceSearchFilterMutation,
  UpdateAudienceSearchFilterMutationVariables,
  AudienceSearchFilterType,
  DeleteAudienceSearchUserFilterMutation,
  DeleteAudienceSearchUserFilterMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  convertTargetingFormToGraphQL,
  TargetGroups,
  DEFAULT_TARGET,
} from "~frontend/components/audienceFilteringForm";
import {
  UPSERT_AUDIENCE_SEARCH_USER_FILTER,
  LIST_AUDIENCE_SEARCH_FILTERS,
  UPDATE_AUDIENCE_SEARCH_FILTER,
  DELETE_AUDIENCE_SEARCH_USER_FILTER,
  GET_AUDIENCE_SEARCH_USER_FILTER,
} from "~frontend/graphql/audience";
import { useSubaccountAdminConfig } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import {
  ReadableTargetGroupSummary,
  SearchResultHeading,
} from "./SavedSearches";
import { SMALL_BUTTON_STYLE } from "./constants";
import { useAudience } from "./store";

export function AudienceFilterForm({
  audienceId,
}: {
  audienceId: string;
}): ReactElement {
  const subaccount = useSubaccountAdminConfig()!;
  const timezone = subaccount.textingHoursTimezone;

  const [upsertAudienceSearchUserFilter] = useMutation<
    UpsertAudienceSearchUserFilterMutation,
    UpsertAudienceSearchUserFilterMutationVariables
  >(UPSERT_AUDIENCE_SEARCH_USER_FILTER, {
    onErrorSnackbarMessage: "Failed to apply filters",
  });

  const [deleteAudienceSearchUserFilter] = useMutation<
    DeleteAudienceSearchUserFilterMutation,
    DeleteAudienceSearchUserFilterMutationVariables
  >(DELETE_AUDIENCE_SEARCH_USER_FILTER, {
    awaitRefetchQueries: true,
    refetchQueries: [GET_AUDIENCE_SEARCH_USER_FILTER],
  });

  const [updateFilter] = useMutation<
    UpdateAudienceSearchFilterMutation,
    UpdateAudienceSearchFilterMutationVariables
  >(UPDATE_AUDIENCE_SEARCH_FILTER, {
    awaitRefetchQueries: true,
    refetchQueries: [LIST_AUDIENCE_SEARCH_FILTERS],
  });

  const { enqueueSnackbar } = useSnackbar();

  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  const currentFilter = useAudience(store => store.currentFilter);
  const setCurrentFilter = useAudience(store => store.setFilter);

  const clearFilters = useAudience(store => store.clearFilters);

  const toggleIsEditingExistingFilter = useAudience(
    store => store.toggleIsEditingExistingFilter
  );

  const formMethods = useForm<TargetingForm & { name: string }>({
    resolver: zodResolver(SavedSearchSchema),
    mode: "all",
    reValidateMode: "onBlur",
    defaultValues: currentFilter,
  });

  const { control, getValues, reset } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: "targetGroups",
  });

  async function handleSaveFilter() {
    const { name, targetGroups } = getValues();

    const { errors, data: updatedFilterResult } = await updateFilter({
      variables: {
        input: {
          audienceId,
          audienceSearchFilterId: currentFilter.id!,
          searchBody: convertTargetingFormToGraphQL({ targetGroups }),
          name,
          type: AudienceSearchFilterType.SavedSearch,
          recalculate: true,
        },
      },
    });

    if (errors) {
      enqueueSnackbar("Failed to save filter", { variant: "error" });
      return;
    }

    const {
      searchBody,
      id,
      name: filterName,
      type: filterType,
      searchJob,
    } = pick(updatedFilterResult?.updateAudienceSearchFilter, [
      "id",
      "searchBody",
      "name",
      "type",
      "searchJob",
    ]);

    setCurrentFilter({
      ...TargetingFormSchema.parse(searchBody),
      id,
      name: filterName,
      type: filterType,
      contactCount: searchJob?.result?.contactCount,
      optedOutContactCount: searchJob?.result?.optedOutContactCount,
    });

    toggleIsEditingExistingFilter();
    enqueueSnackbar("Filter saved successfully", { variant: "success" });
  }

  async function handleApplyFilters() {
    if (isApplyingFilters) {
      return;
    }

    setIsApplyingFilters(true);
    try {
      const formData = getValues();
      const { errors, data } = await upsertAudienceSearchUserFilter({
        variables: {
          input: {
            subaccountId: subaccount.id,
            audienceId,
            searchBody: convertTargetingFormToGraphQL(formData)!,
            recalculate: true,
          },
        },
        awaitRefetchQueries: true,
      });

      if (errors) {
        enqueueSnackbar("Failed to save filter", { variant: "error" });
        return;
      }

      const {
        searchBody,
        id,
        name: filterName,
        type: filterType,
        searchJob,
      } = pick(data?.upsertAudienceSearchUserFilter, [
        "id",
        "searchBody",
        "name",
        "type",
        "searchJob",
      ]);

      setCurrentFilter({
        ...TargetingFormSchema.parse(searchBody),
        id,
        name: filterName,
        type: filterType,
        contactCount: searchJob?.result?.contactCount,
        optedOutContactCount: searchJob?.result?.optedOutContactCount,
      });
      toggleIsEditingExistingFilter();
    } finally {
      setIsApplyingFilters(false);
    }
  }

  async function handleClearUserSearchFilter() {
    const { errors } = await deleteAudienceSearchUserFilter({
      variables: {
        input: {
          subaccountId: subaccount.id,
          audienceId,
        },
      },
    });

    if (errors) {
      enqueueSnackbar("Failed to clear filters", { variant: "error" });
      return;
    }

    clearFilters();
  }

  useEffect(() => {
    reset(currentFilter);
  }, [currentFilter, reset]);

  return (
    <>
      <FormProvider {...formMethods}>
        <FlexBox
          direction="column"
          sx={{
            backgroundColor: palette.lightGray,
            padding: theme.spacing(3),
          }}
        >
          <FlexBox justifyContent="space-between" sx={{ mb: 2 }}>
            {currentFilter.type === AudienceSearchFilterType.SavedSearch ? (
              <>
                <FlexBox direction="column">
                  <SearchResultHeading filter={currentFilter} />
                  <ReadableTargetGroupSummary
                    targetGroups={currentFilter.targetGroups}
                  />
                </FlexBox>
                <FlexBox gap={1} alignItems="flex-end">
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={SMALL_BUTTON_STYLE}
                    onClick={clearFilters}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={SMALL_BUTTON_STYLE}
                    onClick={handleSaveFilter}
                  >
                    Save Changes
                  </Button>
                </FlexBox>
              </>
            ) : (
              <>
                <div />
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={SMALL_BUTTON_STYLE}
                  onClick={handleClearUserSearchFilter}
                >
                  <>
                    <Close sx={{ fontSize: "12px", mr: 1 }} />
                    Clear Filters
                  </>
                </Button>
              </>
            )}
          </FlexBox>
          <TargetGroups
            control={control as unknown as Control<TargetingForm>}
            audienceId={audienceId}
            timezone={timezone}
            targetGroups={fields}
            removeGroup={remove}
            variant="slim"
            savedSearchesEnabled={false}
          />
          <FlexBox gap={2} justifyContent="flex-end">
            <Button
              color="secondary"
              variant="contained"
              sx={SMALL_BUTTON_STYLE}
              onClick={() =>
                append(
                  DEFAULT_TARGET as unknown as TargetingForm["targetGroups"][0]
                )
              }
            >
              + Add Filter Group
            </Button>
            {currentFilter.type === AudienceSearchFilterType.UserSearch ? (
              <Button
                variant="contained"
                color="primary"
                sx={SMALL_BUTTON_STYLE}
                onClick={handleApplyFilters}
                disabled={!formMethods.formState.isValid}
              >
                Apply Filters
              </Button>
            ) : null}
          </FlexBox>
        </FlexBox>
      </FormProvider>
    </>
  );
}
